import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  TreeDeciduous, ArrowLeft, UserPlus, Users, Camera, Calendar, 
  Download, Shield, HelpCircle, CheckCircle, Mail
} from "lucide-react";

const HelpPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <TreeDeciduous className="w-8 h-8 text-primary" />
              <span className="font-display text-xl font-bold text-foreground">Matambanadzo-Gurundoro</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-2">
            How to Use This App
          </h1>
          <p className="text-lg text-muted-foreground">
            Instructions for Matambanadzo-Gurundoro family members
          </p>
        </div>

        {/* Getting Started */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-3">
              <UserPlus className="w-6 h-6 text-primary" />
              Getting Started
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">1</div>
                <div>
                  <p className="font-medium text-foreground">Receive Your Invite</p>
                  <p className="text-muted-foreground">A family admin will send you an invite link via WhatsApp, email, or SMS</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">2</div>
                <div>
                  <p className="font-medium text-foreground">Sign In with Google</p>
                  <p className="text-muted-foreground">Click the invite link and sign in with your Google/Gmail account</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">3</div>
                <div>
                  <p className="font-medium text-foreground">You're In!</p>
                  <p className="text-muted-foreground">You'll automatically be added to the family branch</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Roles */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              Member vs Admin Roles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-foreground">Member</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-600" /> View all family members
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-600" /> Add new family members
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-600" /> Edit member information
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-600" /> Upload photos
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-600" /> Add timeline events
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-600" /> Export family data
                  </li>
                </ul>
              </div>
              <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-accent" />
                  <h3 className="font-semibold text-foreground">Admin</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-green-600" /> Everything members can do
                  </li>
                  <li className="flex items-center gap-2 text-accent">
                    <CheckCircle className="w-4 h-4" /> Invite other family members
                  </li>
                  <li className="flex items-center gap-2 text-accent">
                    <CheckCircle className="w-4 h-4" /> Edit branch settings
                  </li>
                  <li className="flex items-center gap-2 text-accent">
                    <CheckCircle className="w-4 h-4" /> Set privacy controls
                  </li>
                  <li className="flex items-center gap-2 text-accent">
                    <CheckCircle className="w-4 h-4" /> Delete family members
                  </li>
                  <li className="flex items-center gap-2 text-accent">
                    <CheckCircle className="w-4 h-4" /> Delete the branch
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Adding Family Members */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              Adding Family Members
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Click the <strong>"+ Add Member"</strong> button on your branch page and fill in:
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium text-foreground">First & Last Name *</p>
                <p className="text-sm text-muted-foreground">Required fields</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium text-foreground">Birth / Death Date</p>
                <p className="text-sm text-muted-foreground">When they were born/passed</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium text-foreground">Occupation</p>
                <p className="text-sm text-muted-foreground">What they did for work</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium text-foreground">Location</p>
                <p className="text-sm text-muted-foreground">Where they lived</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium text-foreground">Biography</p>
                <p className="text-sm text-muted-foreground">Their life story</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="font-medium text-foreground">Historical Notes</p>
                <p className="text-sm text-muted-foreground">Important stories & memories</p>
              </div>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-secondary/50 border border-border">
              <h4 className="font-semibold text-foreground mb-3">Generation Numbers Guide</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Generation 0:</span><span className="font-medium">Great-grandparents</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Generation 1:</span><span className="font-medium">Grandparents</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Generation 2:</span><span className="font-medium">Parents</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Generation 3:</span><span className="font-medium">Your generation</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Generation 4:</span><span className="font-medium">Children</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Generation 5:</span><span className="font-medium">Grandchildren</span></div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">* Adjust based on your branch's starting point</p>
            </div>
          </CardContent>
        </Card>

        {/* Photos */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-3">
              <Camera className="w-6 h-6 text-primary" />
              Adding Photos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">1</span>
                <span className="text-muted-foreground">Click on a family member's card to open their profile</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">2</span>
                <span className="text-muted-foreground">Click the <strong>"Edit"</strong> button (top right)</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">3</span>
                <span className="text-muted-foreground">Click the <strong>upload icon</strong> on their photo</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">4</span>
                <span className="text-muted-foreground">Select a photo from your device</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">5</span>
                <span className="text-muted-foreground">Click <strong>"Save"</strong></span>
              </li>
            </ol>
            <p className="text-sm text-muted-foreground mt-4 p-3 bg-muted/50 rounded-lg">
              💡 <strong>Tip:</strong> Old photos are precious! Scan physical photos and upload them to preserve our family history.
            </p>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-3">
              <Calendar className="w-6 h-6 text-primary" />
              Adding Timeline Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Track important family moments like births, marriages, deaths, and historical events.
            </p>
            <ol className="space-y-3 mb-4">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">1</span>
                <span className="text-muted-foreground">Go to your branch page and click <strong>"Timeline"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">2</span>
                <span className="text-muted-foreground">Click <strong>"+ Add Event"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">3</span>
                <span className="text-muted-foreground">Select event type, date, and add details</span>
              </li>
            </ol>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm">🍼 Birth</span>
              <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm">⚰️ Death</span>
              <span className="px-3 py-1 rounded-full bg-pink-100 text-pink-800 text-sm">💕 Marriage</span>
              <span className="px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-sm">⭐ Historical</span>
              <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">📅 Custom</span>
            </div>
          </CardContent>
        </Card>

        {/* Inviting Others */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-3">
              <Mail className="w-6 h-6 text-primary" />
              Inviting Family Members (Admins Only)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">1</span>
                <span className="text-muted-foreground">Go to your branch page</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">2</span>
                <span className="text-muted-foreground">Click the <strong>"Invite"</strong> button</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">3</span>
                <span className="text-muted-foreground">Select <strong>Role</strong>: Member or Admin</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">4</span>
                <span className="text-muted-foreground">Click <strong>"Create Invite Link"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium shrink-0">5</span>
                <span className="text-muted-foreground">Click <strong>"Copy Link"</strong> and send via WhatsApp, email, or SMS</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Export */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-3">
              <Download className="w-6 h-6 text-primary" />
              Exporting Family Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Download a backup of your branch's family tree data at any time:
            </p>
            <ol className="space-y-2 mt-3">
              <li className="text-muted-foreground">1. Go to your branch page</li>
              <li className="text-muted-foreground">2. Click the <strong>"Export"</strong> button</li>
              <li className="text-muted-foreground">3. A JSON file will download with all family members and events</li>
            </ol>
          </CardContent>
        </Card>

        {/* Tips */}
        <Card className="bg-accent/5 rounded-2xl border border-accent/20 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-accent">
              💡 Tips for Our Family
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex gap-3">
                <span className="text-accent">•</span>
                <span className="text-foreground"><strong>Add Historical Notes</strong> - Include stories, memories, and historical context for each person</span>
              </li>
              <li className="flex gap-3">
                <span className="text-accent">•</span>
                <span className="text-foreground"><strong>Upload Photos</strong> - Old photos are precious! Scan and upload them</span>
              </li>
              <li className="flex gap-3">
                <span className="text-accent">•</span>
                <span className="text-foreground"><strong>Be Accurate</strong> - Double-check dates and spellings</span>
              </li>
              <li className="flex gap-3">
                <span className="text-accent">•</span>
                <span className="text-foreground"><strong>Coordinate</strong> - Talk to your branch admin to avoid duplicate entries</span>
              </li>
              <li className="flex gap-3">
                <span className="text-accent">•</span>
                <span className="text-foreground"><strong>Export Regularly</strong> - Download backups of your branch data</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8">
          <p className="text-muted-foreground italic">
            Matambanadzo-Gurundoro Family — Our roots run deep 🦁
          </p>
        </div>
      </main>
    </div>
  );
};

export default HelpPage;
